import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, BarChart3, CalendarDays, CheckCircle, Clock, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackAuditButtonClick } from '@/lib/analytics';
import officeBanner from '@/assets/office-banner.jpg';
import { useRef, useState } from 'react';
import CalComCalendar from '@/components/CalComCalendar';

export default function HomePage() {
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef<HTMLDivElement | null>(null);
  return (
    <div className="min-h-screen">
      {/* Hero Section with Background */}
      <div className="relative">
        {/* Hero Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0"
          style={{
            backgroundImage: `url(${officeBanner})`,
          }}
        >
        </div>      
        
        {/* Hero Content */}
        <div className="relative z-20 max-w-5xl mx-auto px-6 py-12 mb-12">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-yellow-200">
              ✨ Free Digital Assessment
            </div>
            <h1 className="text-5xl font-bold leading-tight text-white text-center mb-6">
              Free Digital Efficiency Audit
            </h1>
            <p className="text-responsive-md text-slate-200 mb-12 max-w-4xl mx-auto leading-relaxed">
              Discover hidden inefficiencies in your processes and unlock your organization&apos;s digital potential with our comprehensive assessment. Get actionable insights in just 10 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/audit">
                <Button 
                  id="btn_start_audit_top"
                  size="lg" 
                  className="btn-modern text-lg px-10 py-4 "
                  onClick={() => trackAuditButtonClick('free_audit_top')}
                >
                  Start Your Free Audit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <div className="text-slate-300 text-sm">
                ⚡ Takes only 10 minutes • 📊 Instant results
              </div>
            </div>
           <div className="mt-12">
              <Button
                id="btn_book_walkthrough_top"
                size="lg"
                variant="outline"
                className="text-lg px-8 py-4 bg-white/10 text-white border border-white/20 hover:bg-white/20"
                onClick={() => {
                  setShowCalendar(true);
                  requestAnimationFrame(() => {
                    calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  });
                }}
              >
                Book my free 20 mins walkthrough
              </Button>
            </div>

             {showCalendar && (
              <div ref={calendarRef} className="mt-6">
                <div className="mx-auto max-w-4xl overflow-hidden rounded-3xl border border-white/15 bg-white/95 shadow-2xl">
                  <div className="px-6 sm:px-8 py-6 text-center">
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                        <CalendarDays className="h-5 w-5" />
                      </span>
                      <h3 className="text-xl sm:text-2xl font-bold text-brand-dark">Pick a time (20 mins)</h3>
                    </div>
                    <p className="text-sm sm:text-base text-brand-gray/90 max-w-2xl mx-auto">
                      We'll walk you through your audit results and two quick wins.
                    </p>
                  </div>
                  <div className="w-full">
                    <CalComCalendar />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-responsive-lg font-bold text-brand-dark mb-4">
            Why Choose Our Audit?
          </h2>
          <p className="text-lg text-brand-gray max-w-2xl mx-auto">
            Comprehensive, fast, and actionable insights to transform your business operations
          </p>
        </div>
        
        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          <Card className="text-center glass border-0 shadow-modern interactive-card group">
            <CardHeader className="pb-4">
              <div className="w-16 h-16 gradient-brand rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-modern group-hover:animate-bounce-gentle">
                <BarChart3 className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-xl font-bold text-brand-dark">Comprehensive Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base leading-relaxed text-brand-gray">
                60-point assessment covering all aspects of digital operations
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="text-center glass border-0 shadow-modern interactive-card group">
            <CardHeader className="pb-4">
              <div className="w-16 h-16 gradient-brand-green rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-modern group-hover:animate-bounce-gentle">
                <Target className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-xl font-bold text-brand-dark">Tailored Recommendations</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base leading-relaxed text-brand-gray">
                Get specific, actionable recommendations for your industry and situation
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="text-center glass border-0 shadow-modern interactive-card group">
            <CardHeader className="pb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-slate-500 to-slate-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-modern group-hover:animate-bounce-gentle">
                <Clock className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-xl font-bold text-brand-dark">Quick Results</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base leading-relaxed text-brand-gray">
                Complete the assessment in 10 minutes and get instant results
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="text-center glass border-0 shadow-modern interactive-card group">
            <CardHeader className="pb-4">
              <div className="w-16 h-16 gradient-brand rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-modern group-hover:animate-bounce-gentle">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-xl font-bold text-brand-dark">PDF Report</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base leading-relaxed text-brand-gray">
                Receive a detailed PDF report with implementation roadmap
              </CardDescription>
            </CardContent>
          </Card>
        </div>

        {/* How It Works */}
        <Card className="mb-20 glass border-0 shadow-modern-xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-orange/5 via-transparent to-brand-green/5"></div>
          <div className="relative z-10">
            <CardHeader className="text-center pb-12">
              <CardTitle className="text-responsive-lg mb-6 text-brand-dark font-bold">How It Works</CardTitle>
              <CardDescription className="text-xl text-brand-gray font-medium">Simple 3-step process to transform your business</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid md:grid-cols-3 gap-12">
                <div className="text-center animate-slide-in-left">
                  <div className="w-20 h-20 gradient-brand text-white rounded-3xl flex items-center justify-center font-bold text-2xl mx-auto mb-8 shadow-modern-lg interactive">
                    1
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-brand-dark">Complete the Assessment</h3>
                  <p className="text-brand-gray leading-relaxed text-lg">
                    Answer questions about your current processes, systems, and workflows
                  </p>
                </div>
                <div className="text-center animate-fade-in-up">
                  <div className="w-20 h-20 gradient-brand-green text-white rounded-3xl flex items-center justify-center font-bold text-2xl mx-auto mb-8 shadow-modern-lg interactive">
                    2
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-brand-dark">Get Your Score</h3>
                  <p className="text-brand-gray leading-relaxed text-lg">
                    Receive an instant score and zone classification with detailed analysis
                  </p>
                </div>
                <div className="text-center animate-slide-in-right">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-700 text-white rounded-3xl flex items-center justify-center font-bold text-2xl mx-auto mb-8 shadow-modern-lg interactive">
                    3
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-brand-dark">Implement Changes</h3>
                  <p className="text-brand-gray leading-relaxed text-lg">
                    Follow our roadmap and recommendations to transform your business
                  </p>
                </div>
              </div>
            </CardContent>
          </div>
        </Card>

        {/* CTA Section */}
        <Card className="gradient-hero text-white text-center border-0 shadow-modern-xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-orange/20 via-transparent to-brand-green/20"></div>
          <CardContent className="py-20">
            <div className="relative z-10">
              <h2 className="text-responsive-lg font-bold mb-6">Ready to Unlock Your Digital Potential?</h2>
              <p className="text-xl mb-12 opacity-90 max-w-3xl mx-auto leading-relaxed">
                Join hundreds of businesses that have transformed their operations with our comprehensive digital efficiency audit
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <Link to="/audit">
                  <Button
                    id="btn_start_audit_bottom"
                    size="lg"
                    className="mx-auto inline-flex items-center justify-center gap-3 rounded-xl bg-brand-orange px-12 py-5 text-lg font-semibold text-white shadow-[0_18px_36px_-12px_rgba(244,119,37,0.55)] transition-all duration-300 hover:-translate-y-1 hover:bg-brand-orange/90 hover:shadow-[0_22px_44px_-14px_rgba(244,119,37,0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                    onClick={() => trackAuditButtonClick('free_audit_bottom')}
                  >
                    Start Your Free Audit
                    <ArrowRight className="ml-2 h-6 w-6" />
                  </Button>
                </Link>
                <div className="text-slate-300 text-base">
                  🚀 Free • ⚡ 10 minutes • 📊 Instant results
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
